import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/**
 * A lista de quem pode receber uma carteira — UMA vez, para todas as telas.
 *
 * Antes cada modal montava a sua: umas filtravam por `funcao`, outras por
 * `role`; umas traziam `foto_url`, outras não. O mesmo consultor aparecia com
 * foto na troca individual e sem foto na troca em massa. Aqui a régua é uma só,
 * e vem sempre com o que decide a troca: foto, cargo por extenso, equipe e
 * QUANTOS CLIENTES a pessoa já tem.
 */

/** Cargo em português — chave crua (`CONSULTOR_JUNIOR`) não aparece na tela. */
export const CARGO_LABEL: Record<string, string> = {
  CONSULTOR_JUNIOR: 'Consultor júnior',
  CONSULTOR_PLENO: 'Consultor pleno',
  CONSULTOR_SENIOR: 'Consultor sênior',
  CONSULTOR_RETENCAO: 'Consultor retenção',
  SUPERVISORA_VENDAS: 'Supervisor(a) de vendas',
  SUPERVISORA_BACKOFFICE: 'Supervisor(a) de backoffice',
  GERENTE_COMERCIAL: 'Gerente comercial',
  COORDENADOR_COMERCIAL: 'Coordenador comercial',
  COORDENADOR_VENDAS: 'Coordenador de vendas',
  COORDENADOR_OPERACIONAL: 'Coordenador operacional',
  DIRETOR_COMERCIAL: 'Diretor comercial',
  DIRETORA_ADMINISTRATIVA: 'Diretora administrativa',
  GESTOR_PROJETOS: 'Gestor de projetos',
  ANALISTA_QUALIDADE: 'Analista de qualidade',
  ANALISTA_QUALIDADE_2: 'Analista de qualidade 2',
  ANALISTA_QUALIDADE_3: 'Analista de qualidade 3',
  BKO_CONTRATO: 'BKO contrato',
  BKO_INPUT: 'BKO tramitação',
  BKO_TRAMITACAO: 'BKO crédito',
  BKO_PORTABILIDADE: 'BKO portabilidade',
  BKO_COMPLETO: 'BKO completo',
  RH_DEPARTAMENTO_PESSOAL: 'RH / Departamento Pessoal',
};

export const cargoPorExtenso = (funcao?: string | null, role?: string | null) => {
  if (funcao && CARGO_LABEL[funcao]) return CARGO_LABEL[funcao];
  if (funcao) return funcao.replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase());
  if (role) return role.charAt(0).toUpperCase() + role.slice(1);
  return 'Sem cargo';
};

export interface ConsultorElegivel {
  id: string;
  nome: string;
  foto_url: string | null;
  funcao: string | null;
  role: string | null;
  cargo: string;
  equipeId: string | null;
  equipeNome: string | null;
  /** Quantos clientes já estão na carteira dele — decide para quem passar. */
  qtdClientes: number;
}

type Escopo = 'comercial' | 'todos';

export function useConsultoresElegiveis(opts?: {
  escopo?: Escopo;
  /** Limita a uma equipe (o supervisor só transfere dentro da dele). */
  equipeId?: string | null;
  /** Tira alguém da lista — normalmente o dono atual. */
  excluirId?: string | null;
  habilitado?: boolean;
}) {
  const { escopo = 'comercial', equipeId = null, excluirId = null, habilitado = true } = opts || {};

  return useQuery({
    queryKey: ['consultores-elegiveis', escopo, equipeId],
    enabled: habilitado,
    staleTime: 2 * 60_000,
    queryFn: async (): Promise<ConsultorElegivel[]> => {
      let q = supabase
        .from('usuarios')
        .select('id, nome, foto_url, funcao, role, equipe_id, equipes:equipe_id(nome)')
        .eq('ativo', true)
        .order('nome');
      if (equipeId) q = q.eq('equipe_id', equipeId);
      const { data, error } = await q;
      if (error) throw error;

      // A carga de cada um: uma consulta só, agregada em memória. Sem isso a
      // tela pedia para escolher entre dois nomes sem dizer quem está cheio.
      const { data: donos } = await supabase
        .from('donos_cliente')
        .select('owner_consultor_id')
        .not('owner_consultor_id', 'is', null);
      const carga = new Map<string, number>();
      for (const d of (donos as { owner_consultor_id: string }[] | null) || []) {
        carga.set(d.owner_consultor_id, (carga.get(d.owner_consultor_id) ?? 0) + 1);
      }

      const PODEM_TER_CARTEIRA = /^(CONSULTOR_|SUPERVISORA_VENDAS|COORDENADOR_|GERENTE_COMERCIAL|DIRETOR)/;

      return ((data as any[]) || [])
        .filter((u) => {
          if (escopo !== 'comercial') return true;
          // Quem pode receber carteira comercial. Mantém a régua que o modal
          // individual já usava (funcao), com os gestores incluídos.
          return u.funcao ? PODEM_TER_CARTEIRA.test(u.funcao) : ['consultor', 'supervisor', 'gerente'].includes(String(u.role));
        })
        .map((u) => ({
          id: u.id,
          nome: u.nome,
          foto_url: u.foto_url ?? null,
          funcao: u.funcao ?? null,
          role: u.role ?? null,
          cargo: cargoPorExtenso(u.funcao, u.role),
          equipeId: u.equipe_id ?? null,
          equipeNome: u.equipes?.nome ?? null,
          qtdClientes: carga.get(u.id) ?? 0,
        }))
        .filter((u) => u.id !== excluirId);
    },
  });
}
